import { describeVariant } from '@sitzmark/core';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { api, errorMessage } from '../lib/api.js';
import { useTypingFocus } from '../lib/typing-focus.js';
import { Mono } from './ui.js';
import './item-search.css';

export type FoundItem = Awaited<ReturnType<typeof api.catalog.search.query>>['results'][number];

/** As many as the API gives, in a list that scrolls — the register's rule (FB-1071). */
const LIMIT = 50;

/**
 * Find an item by scanning it or typing (2026-10-02): "I tried to search on
 * the return to vendor and it didn't pull up things until I pressed enter …
 * only showing 8 results, can we let that list scroll like we do on register."
 *
 * Results appear as you type, up to fifty, in a list that scrolls. A scan — an
 * exact SKU or barcode — goes straight in, as it does at the register; Enter
 * asks the server for exactly what is in the box rather than trusting a list
 * that may still be catching up with a scanner's keystrokes. One component for
 * every bench that adds items one at a time: returns to vendor and receiving
 * without an order.
 */
export function ItemSearch({
  inputRef,
  label,
  placeholder,
  onPick,
  nothingFound,
}: {
  inputRef?: RefObject<HTMLInputElement | null>;
  /** The box's accessible name. */
  label: string;
  placeholder: string;
  onPick: (item: FoundItem) => void;
  /** What to say when nothing matches what was typed. */
  nothingFound: (term: string) => string;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  /** Typing or scanning anywhere on the screen lands here (client, 2026-10-15). */
  const ownRef = useRef<HTMLInputElement>(null);
  const boxRef = inputRef ?? ownRef;
  const append = useCallback((character: string) => setTerm((current) => current + character), []);
  useTypingFocus(boxRef, append);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 120);
    return () => clearTimeout(timer);
  }, [term]);

  const search = useQuery({
    queryKey: ['catalog', 'search', debounced, LIMIT],
    queryFn: () => api.catalog.search.query({ term: debounced, limit: LIMIT }),
    enabled: debounced.length > 0,
    staleTime: 10_000,
  });

  const clear = () => {
    setTerm('');
    setDebounced('');
  };

  const pick = (item: FoundItem) => {
    setProblem(null);
    clear();
    onPick(item);
  };

  // A scan names exactly one item: straight in, no list of one to pick from.
  useEffect(() => {
    const data = search.data;
    if (data?.scanned === true && data.results[0] !== undefined && debounced === term.trim()) {
      pick(data.results[0]);
    }
    // Only a new answer acts; `pick` and `term` are read as they are when it lands.
  }, [search.data]);

  /** Enter: ask for exactly what is in the box now. */
  const lookUpNow = async () => {
    const cleaned = term.trim();
    if (cleaned === '') return;
    try {
      const found = await api.catalog.search.query({ term: cleaned, limit: LIMIT });
      if (found.scanned && found.results[0] !== undefined) pick(found.results[0]);
      else if (found.results.length === 1) pick(found.results[0]!);
      else if (found.results.length === 0) setProblem(nothingFound(cleaned));
      else setDebounced(cleaned);
    } catch (error) {
      setProblem(errorMessage(error));
    }
  };

  const results = debounced === '' ? [] : (search.data?.results ?? []);
  /*
   * A purchase order's number typed here (FB-1132: "Return to vendor when
   * type in PO number — not work"). The box adds items; it said nothing, and
   * the number's digits matched items at random.
   */
  const orderNumber = /^(PO|R)-\d+$/i.test(term.trim()) ? term.trim().toUpperCase() : null;
  const empty =
    debounced !== '' && search.data !== undefined && results.length === 0 && term.trim() !== '';

  return (
    <div className="item-search">
      <input
        ref={boxRef}
        className="search__input"
        placeholder={placeholder}
        aria-label={label}
        value={term}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => {
          setTerm(event.target.value);
          setProblem(null);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void lookUpNow();
          } else if (event.key === 'Escape') {
            clear();
          }
        }}
      />
      {orderNumber !== null ? (
        <p className="item-search__problem" role="status">
          {orderNumber} is a purchase order number. This box adds items: scan each item, or type its
          SKU, barcode or name.
        </p>
      ) : problem !== null ? (
        <p className="item-search__problem" role="status">
          {problem}
        </p>
      ) : empty ? (
        <p className="item-search__problem" role="status">
          {nothingFound(debounced)}
        </p>
      ) : null}
      {results.length > 0 ? (
        <ul className="item-search__results" aria-label={`Matches for ${debounced}`}>
          {results.map((item) => {
            const name = describeVariant({
              productName: item.name,
              colorName: item.color,
              sizeLabel: item.size,
              altSize: item.altSize,
            });
            return (
              <li key={item.variantId}>
                <button type="button" className="item-search__result" onClick={() => pick(item)}>
                  <span className="item-search__name">{name}</span>
                  <span className="text-secondary">
                    <Mono>{item.sku}</Mono>
                    {item.brand === null ? '' : ` · ${item.brand}`}
                    {item.available === null ? '' : ` · ${item.available} available`}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
